import postgrestRestProvider, { IDataProviderConfig } from '@raphiniert/ra-data-postgrest'
import { type DataProvider, fetchUtils, HttpError } from 'react-admin'
import { z } from 'zod'

import { useAuthStore } from './stores/auth-store'

interface FetchOptions {
  headers?: Record<string, string> | Headers
}

const httpClient = (url: string, options: FetchOptions = {}) => {
  const session = useAuthStore.getState().session

  // Add Supabase authentication headers
  const headers = new Headers({
    ...(options.headers instanceof Headers ? Object.fromEntries(options.headers.entries()) : options.headers),
    apikey: String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''),
    Authorization: `Bearer ${session?.access_token ?? ''}`,
    Prefer: 'count=exact', // Add this to get total count
  })
  return fetchUtils.fetchJson(url, { ...options, headers })
}

const config: IDataProviderConfig = {
  // apiUrl is not used for historical_inspections RPC call
  apiUrl: `${String(import.meta.env.VITE_SUPABASE_URL ?? '')}/rest/v1`,
  httpClient,
  defaultListOp: 'eq',
  primaryKeys: new Map([
    // The id is still the primary key for the returned data
    ['historical_inspections', ['id']],
    ['inspections', ['id']],
  ]),
  schema: () => 'public',
}

const postgrestProvider = postgrestRestProvider(config)

/**
 * Zod schema to safely extract inspection-specific filter fields from the
 * untyped React Admin filter object (typed as `any` in GetListParams).
 */
const inspectionFilterSchema = z
  .object({
    container: z.string().optional(),
    day: z.union([z.string(), z.instanceof(Date)]).optional(),
    services: z.string().optional(),
  })
  .catchall(z.unknown())

type InspectionFilter = z.infer<typeof inspectionFilterSchema>

/**
 * Transform inspection filter fields into ra-data-postgrest operator syntax.
 * - `container` → `container@ilike` (case-insensitive partial match)
 * - `day` → `day@eq` (exact date match, formatted as YYYY-MM-DD)
 * - `services` → `services@cs` (array containment, so a row carrying the chosen
 *   service alongside others still matches; the default `eq` would only match a
 *   row whose services are exactly that one)
 * - All other fields are passed through unchanged.
 */
const buildInspectionFilter = (filter: InspectionFilter): Record<string, unknown> => {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(filter)) {
    if (key !== 'container' && key !== 'day' && key !== 'services' && value !== undefined && value !== null && value !== '') {
      result[key] = value
    }
  }

  if (filter.services) {
    // PostgREST spells the right-hand side of `cs` on an array column as a
    // Postgres array literal, which is what the braces are.
    result['services@cs'] = `{${filter.services}}`
  }

  if (filter.container) {
    result['container@ilike'] = filter.container.toUpperCase()
  }

  if (filter.day !== undefined) {
    const dateValue = filter.day
    let formattedDate: string

    if (dateValue instanceof Date) {
      formattedDate = dateValue.toISOString().split('T')[0] ?? ''
    } else {
      const parsedDate = new Date(dateValue)
      formattedDate = parsedDate.toISOString().split('T')[0] ?? ''
    }

    result['day@eq'] = formattedDate
  }

  return result
}

/**
 * PostgREST filter fragment that excludes soft-deleted rows. Merged into every
 * read of historical_inspections so a record with `is_deleted = true` never
 * surfaces in any view (list or detail).
 */
const NOT_DELETED_FILTER = { 'is_deleted@eq': false }

export const dataProvider: DataProvider = {
  ...postgrestProvider,
  // Only the read paths reachable from the current UI (getList for the list,
  // getOne for the detail) filter out soft-deleted historical_inspections.
  // getMany / getManyReference are inherited unfiltered: no ReferenceField in the
  // app targets historical_inspections today, so there is no live leak. If one is
  // added, override those here to inject NOT_DELETED_FILTER as well.
  getList: async (resource, params) => {
    // Enhanced filtering for historical_inspections using postgREST operator syntax.
    // The soft-delete filter is merged in last so a user-supplied filter cannot drop
    // the is_deleted@eq constraint (no UI filter targets is_deleted).
    if (resource === 'historical_inspections') {
      const filterResult = inspectionFilterSchema.safeParse(params.filter)
      const inspectionFilter = filterResult.success ? buildInspectionFilter(filterResult.data) : {}

      return postgrestProvider.getList(resource, {
        ...params,
        filter: { ...inspectionFilter, ...NOT_DELETED_FILTER },
      })
    }

    // Default behavior for other resources
    return postgrestProvider.getList(resource, params)
  },
  getOne: async (resource, params) => {
    // getOne has no filter parameter, so we first confirm via a filtered getList
    // that the record exists and is not soft-deleted. A missing (or deleted)
    // record rejects with 404 so React Admin shows "not found" instead of
    // rendering the deleted inspection. The actual fetch is delegated to the base
    // provider to preserve React Admin's generic typing.
    if (resource === 'historical_inspections') {
      const { data } = (await postgrestProvider.getList(resource, {
        filter: { 'id@eq': params.id, ...NOT_DELETED_FILTER },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      })) as { data: unknown[] }

      if (data.length === 0) {
        return Promise.reject(new HttpError('historical_inspection not found', 404))
      }
    }

    return postgrestProvider.getOne(resource, params)
  },
}
